import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import Header from "user/components/common/Header";
import Logo from "user/components/common/Logo";
import { non_member_term } from "./-components/terms";
import { useAuthStore } from "user/store/useAuthStore";
import { useNonMemberStore } from "user/store/useNonMemberStore";
import useOverlay, { OverlayWrapper } from "common/hooks/user/useOverlay";
import { CompanySearchOverlay } from "user/components/common/Overlay";
import { useGetCommonTargetCustomer } from "common/hooks/common/useGetCommonTargetCustomer";
import { ReactComponent as Company } from "../../assets/icons/company.svg";
import { ReactComponent as NonCheck } from "../../assets/icons/nonCheck.svg";
import { ReactComponent as RightArrow } from "../../assets/icons/rightArrow.svg";
import { ReactComponent as SelectArrow } from "../../../common/assets/icons/selectIcon.svg";
import loginBG from "../../assets/images/loginBG.png";

export type OverlayProps = {
    open: boolean;
    content: ReactNode | null;
};

type NonMemberFormikProps = {
    customer_name: string;
    customer_idx: null | number;
    term_check: boolean;
};

export default function LoginNonMember() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const user = useAuthStore(state => state);
    const nonMember = useNonMemberStore(state => state);
    const { overlay, closeOverlay, openOverlay } = useOverlay();

    const linkedCustomer = useMemo(() => {
        const t = searchParams.get("t");
        if (t) {
            const idx = parseInt(t, 36);
            if (!Number.isNaN(idx) && idx > 0) return { i: idx, n: "" };
        }
        const token = searchParams.get("token");
        if (token) {
            try {
                return JSON.parse(decodeURIComponent(atob(token))) as { i: number; n: string };
            } catch {
                return null;
            }
        }
        return null;
    }, [searchParams]);

    const isFromLink = linkedCustomer !== null;

    const { data: linkedCustomerData } = useGetCommonTargetCustomer(linkedCustomer?.i ?? 0);

    useEffect(() => {
        nonMember.clearData();
    }, []);

    const formik = useFormik<NonMemberFormikProps>({
        initialValues: {
            customer_name: linkedCustomer?.n ?? "",
            customer_idx: linkedCustomer?.i ?? null,
            term_check: false,
        },
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(true);
            user.clearAuth();

            nonMember.setData({
                customer_name: values.customer_name,
                customer_idx: values.customer_idx,
            });

            navigate("/new-order");
        },
        validate: values => {
            const errors: Record<string, string> = {};
            if (!values.customer_name) {
                errors.customer_name = "부서를 선택해주세요.";
            }
            if (!values.term_check) {
                errors.term_check = "필수 항목 체크해주세요.";
            }

            return errors;
        },
        validateOnMount: true,
    });

    const fetchedCustomerName = linkedCustomerData?.data?.customer_name;
    useEffect(() => {
        if (fetchedCustomerName && formik.values.customer_name !== fetchedCustomerName) {
            formik.setFieldValue("customer_name", fetchedCustomerName);
        }
    }, [fetchedCustomerName]);

    const [isTermsOpen, setIsTermsOpen] = useState(false);

    const errorKeys = Object.keys(formik.errors);
    const isInValid = errorKeys.length > 0;

    return (
        <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
            <div
                className={`relative flex flex-col bg-cover bg-no-repeat bg-center min-h-[100dvh]`}
                style={{
                    backgroundImage: `url(${loginBG})`,
                }}
            >
                <Header goBack />

                <div className="w-full flex flex-col items-center [&_form]:w-full px-[40px] pt-[160px] mobile450:pt-[40px] mobile450:px-[28px] pb-[190px] mobile450:pb-[110px] gap-[60px] mobile450:gap-[40px]">
                    <div className="flex flex-col items-center gap-[20px]">
                        <Logo className="mx-auto" />
                        <p className="text-lg text-primary1 text-center">
                            현재 고려원예에 등록된 회사와 부서만 <br /> 비회원 이용이 가능합니다.
                        </p>
                    </div>

                    <form onSubmit={formik.handleSubmit}>
                        <div
                            onClick={() =>
                                !isFromLink &&
                                openOverlay(
                                    <CompanySearchOverlay
                                        category={1}
                                        onClickClose={closeOverlay}
                                        onClickTarget={({ name, idx }: { name: string; idx: number }) => {
                                            formik.setValues({
                                                ...formik.values,
                                                customer_idx: idx,
                                                customer_name: name,
                                            });
                                            closeOverlay();
                                        }}
                                    />,
                                )
                            }
                        >
                            <div
                                className={`relative flex justify-between items-center px-3 rounded-lg h-[52px] border border-gray4 ${
                                    isFromLink
                                        ? "bg-gray-100 cursor-not-allowed [&_*]:cursor-not-allowed opacity-70"
                                        : `bg-white cursor-pointer [&_*]:cursor-pointer ${formik.touched.customer_name && errorKeys.includes("customer_name") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`
                                }`}
                            >
                                <div className="flex-1 flex items-center gap-[10px]">
                                    <Company className="shrink-0" />
                                    <input
                                        name="customer_name"
                                        value={formik.values.customer_name}
                                        onChange={formik.handleChange}
                                        placeholder="회사와 부서를 선택해주세요."
                                        readOnly
                                        className={`w-full focus:outline-none text-lg placeholder:text-[#999999] text-gray2 ${isFromLink ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
                                    />
                                </div>
                                {!isFromLink && <SelectArrow />}
                            </div>
                        </div>
                        {!isFromLink && formik.errors.customer_name && formik.touched.customer_name && (
                            <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">{formik.errors.customer_name}</div>
                        )}

                        <div className="mt-[60px] flex flex-col gap-[12px]">
                            <div
                                className="p-[12px] flex gap-2 items-center justify-between px-1 cursor-pointer"
                                onClick={() => setIsTermsOpen(prev => !prev)}
                            >
                                <div className="flex items-center gap-2">
                                    <NonCheck className={`${formik.values.term_check && "[&_path]:stroke-[#4B632B]"}`} />
                                    <p className="text-lg">[필수] 비회원 이용약관</p>
                                </div>
                                <SelectArrow className={`transition-transform duration-300 ${isTermsOpen ? "rotate-180" : ""}`} />
                            </div>

                            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isTermsOpen ? "max-h-[400px]" : "max-h-0"}`}>
                                <div className="flex flex-col gap-[12px] px-1 pb-[12px]">
                                    <div
                                        className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 overflow-y-auto max-h-[260px] leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: non_member_term.content }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            formik.setFieldValue("term_check", true);
                                            setIsTermsOpen(false);
                                        }}
                                        className="bg-[#273219] w-full rounded-[12px] text-white text-[18px] leading-[28px] py-[12px] font-bold"
                                    >
                                        동의하기
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isInValid}
                                className={`shadow-custom bg-gradient-to-r from-[#273219] to-[#4B632B] text-white font-bold text-[18px] leading-[28px] py-[12px] rounded-[12px] text-center disabled:opacity-[24%]`}
                            >
                                비회원 이용하기
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </OverlayWrapper>
    );
}
